"use client";

import { useEffect, useState } from "react";
import type { OpeningHoursBundle } from "@restaurant/db";
import styles from "./HorairesPageView.module.css";

interface HorairesPageViewProps {
  initialBundle: OpeningHoursBundle;
  locale: string;
  copy: {
    title: string;
    lead: string;
    scheduleAriaLabel: string;
    exceptionsTitle: string;
    exceptionsAriaLabel: string;
  };
}

export function HorairesPageView({ initialBundle, locale, copy }: HorairesPageViewProps) {
  const [bundle, setBundle] = useState(initialBundle);

  useEffect(() => {
    let isMounted = true;

    async function refreshHours() {
      try {
        const response = await fetch(`/api/opening-hours?locale=${encodeURIComponent(locale)}`, { cache: "no-store" });
        if (!response.ok) return;
        const nextBundle = (await response.json()) as OpeningHoursBundle;
        if (isMounted) setBundle(nextBundle);
      } catch {
        // Keep the last known hours visible if the realtime refresh fails.
      }
    }

    const interval = setInterval(refreshHours, 2000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [locale]);

  return (
    <main className={styles.page}>
      <section className={styles.hero}>
        <p className={styles.eyebrow}>Le B Brasserie</p>
        <h1>{copy.title}</h1>
        <p className={styles.lead}>{copy.lead}</p>
      </section>

      <section className={`${styles.statusCard} ${bundle.currentStatus.isOpen ? styles.open : styles.statusClosed}`}>
        <span aria-hidden="true" />
        <div>
          <strong>{bundle.currentStatus.label}</strong>
          <p>{(locale === "fr" ? bundle.messages.homeMessageFr : null) || bundle.currentStatus.detail}</p>
        </div>
      </section>

      <section className={styles.card} aria-label={copy.scheduleAriaLabel}>
        {bundle.hours.map((hour) => (
          <div className={styles.row} key={hour.dayOfWeek}>
            <span className={styles.day}>{hour.dayLabel}</span>
            <span className={hour.isClosed ? styles.closed : styles.hours}>{hour.displayLabel}</span>
          </div>
        ))}
      </section>

      {bundle.exceptions.length > 0 && (
        <section className={styles.exceptions} aria-label={copy.exceptionsAriaLabel}>
          <h2>{copy.exceptionsTitle}</h2>
          {bundle.exceptions.map((exception) => (
            <p key={exception.id}>
              <strong>{exception.dayLabel} {exception.monthLabel}</strong>
              <span>{(locale === "fr" ? exception.messageFr : null) || exception.typeLabel}</span>
            </p>
          ))}
        </section>
      )}
    </main>
  );
}
